import { useRef, useEffect, memo, Fragment, useMemo } from 'react';
import moment from 'moment';
import Checkbox from '@material-ui/core/Checkbox';
import { generatePDF } from '@abhisek507/html2multipagepdf';
import {
  Letter,
  Quality as ImageQuality,
  RegularPageSelector as pageSelector,
  PageElementSelector as pageElementSelector,
  PageWithMaxPossibleWidthSelector as pageWithMaxPossibleWidthSelector,
} from '@abhisek507/html2multipagepdf/constants';
import Input from '../../subComponents/input';
import Button from '../../subComponents/button';
import DateWrapper from '../../subComponents/date-wrapper';
import {
  SectionLabel, SignatureWrapper,
  trimDots,
  SOAPNotes,
} from '../Eversense';
import { Dexcom } from '../../../constants/patientChart.constant';
import {
  CMNDexcomTemplateIdentifier,
  DEFAULT_PDF_INFO, MaxDate, DateFormat,
  CheckBoxValue, CheckBoxChecked, QID_CGM, QID_HbA1CInput,
  QID_PumpCheckbox, QID_Fingersticks, QID_InjectInhaleInputs,
  QID_FastingHyperglycemiaInputs,
  QID_BGLevelHighInputs,
  QID_BGLevelLowInputs,
  QID_PatientOnInsulinCheckbox,
  QID_3InjectionsPDCheckbox,
  QID_HypoglycemiaUnawarenessSymptomsCheckbox,
  QID_FrequentAdjustmentsInTreatmentCheckbox,
  QID_PatientSelfChecksBGCheckbox,
  MaxDateAndTimeFn,
  // eslint-disable-next-line no-unused-vars
  CMN_PDF_DATA,
} from '../../../constants/cmnTemplateForDexcom.constant';
import { DEFAULT_FORMFIELD, PID_ProvidersAndCreatePDF } from '../../../constants/cmnTemplateForEversense.constant';
import { preparePDF } from '../../../utils/referralForm';
import { useTime } from '../../../hooks/useTime';
import DexcomLogo from '../../../assets/dexcomLogo.png';
import './styles.css';

const evaluateAnswerFromHbA1CInput = (qAndA) => {
  try {
    const regex = /[^0-9.\s]/gm;
    const text = (qAndA.AnswerText || '').replace(regex, '');
    const trimmedText = trimDots(text.trim());
    const answer = !isNaN(trimmedText) ? Number(trimmedText) : 0;
    return { hasAnswer: (answer >= 4 && answer <= 10), answer };
  } catch(e) {
    return { hasAnswer: false, answer: 0 };
  }
};

const evaluateAnswerFromFingerstricksInput = (qAndA = { AnswerText: '' }) => {
  const expressions = [
    {
      regex: /(using a cgm)/gmi,
      response: { hasAnswer: true, answer: 'N/A' },
    },
    {
      regex: /(0 time)/gmi,
      response: { hasAnswer: true, answer: '0' },
    },
    {
      regex: /(1 time)/gmi,
      response: { hasAnswer: true, answer: '1' },
    },
    {
      regex: /(2 time)/gmi,
      response: { hasAnswer: true, answer: '2' },
    },
    {
      regex: /(3 time)/gmi,
      response: { hasAnswer: true, answer: '3' },
    },
    {
      regex: /(4 time)/gmi,
      response: { hasAnswer: true, answer: '4' },
    },
    {
      regex: /(5 time)/gmi,
      response: { hasAnswer: true, answer: '5' },
    },
    {
      regex: /(6 time)/gmi,
      response: { hasAnswer: true, answer: '6+' },
    },
  ];

  for (let i = 0; i < expressions.length; i++) {
    const { regex, response } = expressions[i];
    if (regex.test(qAndA.AnswerText)) {
      return response;
    }
  }

  return { hasAnswer: false, answer: '' };
};

const evaluateAnswerFromInjectionsInput = (qAndA = { AnswerText: '' }) => {
  const expressions = [
    {
      regex: /(insulin using a pump)/gmi,
      response: { hasAnswer: true, answer: 'N/A' },
    },
    {
      regex: /(0 time)/gmi,
      response: { hasAnswer: true, answer: '0' },
    },
    {
      regex: /(1 time)/gmi,
      response: { hasAnswer: true, answer: '1' },
    },
    {
      regex: /(2 time)/gmi,
      response: { hasAnswer: true, answer: '2' },
    },
    {
      regex: /(3 time)/gmi,
      response: { hasAnswer: true, answer: '3' },
    },
    {
      regex: /(4 time)/gmi,
      response: { hasAnswer: true, answer: '4' },
    },
    {
      regex: /(5 time)/gmi,
      response: { hasAnswer: true, answer: '5' },
    },
    {
      regex: /(6 time)/gmi,
      response: { hasAnswer: true, answer: '6+' },
    },
  ];

  for (let i = 0; i < expressions.length; i++) {
    const { regex, response } = expressions[i];
    if (regex.test(qAndA.AnswerText)) {
      return response;
    }
  }

  return { hasAnswer: false, answer: '' };
};

const evaluateAnswerFromFastingHyperglycemiaInput = (qAndA) => {
  try {
    const regex = /[^0-9.\s]/gm;
    const text = (qAndA.AnswerText || '').replace(regex, '');
    const trimmedText = trimDots(text.trim());
    const answer = !isNaN(trimmedText) ? Number(trimmedText) : 0;
    return { hasAnswer: (answer >= 20 && answer <= 400), answer };
  } catch(e) {
    return { hasAnswer: false, answer: 0 };
  }
};

const evaluateAnswerFromPatientOnInsulinCheckbox = (qAndA = { AnswerText: '' }) => {
  const expressions = [
    {
      regex: /(insulin using a pump|short acting insulin|long acting insulin|inhaled insulin)/gmi,
      response: { hasAnswer: true, answer: true },
    },
  ];

  for (let i = 0; i < expressions.length; i++) {
    const { regex, response } = expressions[i];
    if (regex.test(qAndA.AnswerText)) {
      return response;
    }
  }

  return { hasAnswer: false, answer: false };
};

const evaluateAnswerFrom3InjectionsPDCheckbox = (qAndA = { AnswerText: '' }) => {
  const expressions = [
    {
      regex: /(insulin using a pump|3 time|4 time|5 time|6 time)/gmi,
      response: { hasAnswer: true, answer: true },
    },
  ];

  for (let i = 0; i < expressions.length; i++) {
    const { regex, response } = expressions[i];
    if (regex.test(qAndA.AnswerText)) {
      return response;
    }
  }

  return { hasAnswer: false, answer: false };
};

const evaluateAnswerFromPatientSelfChecksBGCheckbox = (qAndA = { AnswerText: '' }) => {
  const expressions = [
    {
      regex: /(using a cgm|4 time|5 time|6 time)/gmi,
      response: { hasAnswer: true, answer: true },
    },
  ];

  for (let i = 0; i < expressions.length; i++) {
    const { regex, response } = expressions[i];
    if (regex.test(qAndA.AnswerText)) {
      return response;
    }
  }

  return { hasAnswer: false, answer: false };
};

const evaluateAnswerFromFAITRCheckbox = (qAndA = { AnswerText: '' }) => {
  const expressions = [
    {
      regex: /(yes)/gmi,
      response: { hasAnswer: true, answer: true },
    },
  ];

  for (let i = 0; i < expressions.length; i++) {
    const { regex, response } = expressions[i];
    if (regex.test(qAndA.AnswerText)) {
      return response;
    }
  }

  return { hasAnswer: false, answer: false };
};

const evaluateAnswerFromHHUCheckbox = (qAndA = { AnswerText: '' }) => {
  const expressions = [
    {
      regex: /I have recurring episodes of severe hypoglycemia \(low blood sugar\)/gmi,
      response: { hasAnswer: true, answer: true },
    },
    {
      regex: /When my blood sugar drops under 80 mg\/dl, I often do not experience symptoms/gmi,
      response: { hasAnswer: true, answer: true },
    },
    {
      regex: /I have had low blood sugar that required medical assistance \(including calling 911 or going to the ER\)/gmi,
      response: { hasAnswer: true, answer: true },
    },
  ];

  for (let i = 0; i < expressions.length; i++) {
    const { regex, response } = expressions[i];
    if (regex.test(qAndA.AnswerText)) {
      return response;
    }
  }

  return { hasAnswer: false, answer: false };
};

const Heading = memo(({
  value: {
    sentTo,
    sentDate,
  },
}) => (
  <div className='heading flex align-items-center justify-content-space-between m-b-1'>
    <div className='flex min-width-160 width-160 heading-logo'>
      <div className='flex align-items-center justify-content-center'>
        <img src={DexcomLogo} alt='leva_logo' className='height-30 width-160' />
      </div>
    </div>

    <div className='flex flex-column min-width-40 align-items-center'>
      <span className='bold letter-spacing-1'>DEXCOM ORDER FORM</span>
      <span className='bold letter-spacing-1'>(CERTIFICATE OF MEDICAL NECESSITY)</span>
    </div>

    <div className='flex min-width-160' />
  </div>
));

const Footer = memo(({
  onSubmit: handleSubmit = () => {}
}) => (
  <div className='footer flex flex-column gap-16'>
    <p className='flex font-10 line-14 m-0'>
      * INFORMATION CONTAINED WITHIN THIS MESSAGE MAY BE PRIVILEGED AND CONFIDENTIAL INFORMATION. IT IS INTENDED FOR THE USE OF THE INDIVIDUAL OR ENTITY TO WHICH IT IS ADDRESSED. IF THE READER OF THIS MESSAGE IS NOT THE INTENDED RECIPIENT, YOU ARE HEREBY NOTIFIED THAT ANY USE, DISSEMINATION, DISCLOSURE OR COPYING OF THIS MESSAGE IS STRICTLY PROHIBITED. IF YOU HAVE RECEIVED THIS INFORMATION IN ERROR PLEASE NOTIFY US IMMEDIATELY AND THEN DESTROY THIS MESSAGE. FOR QUESTIONS REGARDING CONFIDENTIALITY, PLEASE CALL (888) 738-3646.
    </p>
    <div className='flex justify-content-flex-end d-none'>
      <Button
        variant='contained'
        color='primary'
        onClick={handleSubmit}
        label='Download Template'
      />
    </div>
  </div>
));

const ScriptInfo = memo(() => (
  <div className='script-info d-none'>
    <div className='flex align-items-center justify-content-center bold font-16'>
      For easier processing, please use blue or black ink and use block lettering.
    </div>
  </div>
));

const PatientNameAndDOB = memo(({
  value: {
    patientFullName,
    patientDob,
  },
  bottonGutter = '',
  onChange: handleChange = () => {},
}) => (
  <div className={`flex align-items-center ${bottonGutter}`}>
    <Input name='patientFullName' label='Patient Last, First Name:' className='flex flex-fill m-r-4' value={patientFullName} onChange={handleChange.bind(this, 'patientFullName')} />
    <DateWrapper name='patientDob' label='Date of Birth:' value={patientDob} onChange={handleChange.bind(this, 'patientDob')} />
  </div>
));

const PatientInformation = memo(({
  value: {
    patientDob,
    patientFullName,
    patientGender,
    patientHeight,
    patientWeight,
    patientAddress,
    patientState,
    patientZip,
    patientCity,
    patientPhone,
    patientInsuranceCompany,
    patientInsuranceNo,
    patientEmail,
    patientDexcomId,
    primaryLanguageEnglish,
    ...value
  },
  onChange: handleChange = () => {},
}) => (
  <div className={`section patient-information m-t-8 p-1`}>
    <SectionLabel display='PATIENT INFORMATION' />

    <div className='flex flex-column gap-4'>
      <PatientNameAndDOB value={{ patientFullName, patientDob }} bottonGutter='' onChange={handleChange} />

      <div className='flex align-items-center'>
        <div className='flex flex-fill align-items-center gap-16 m-r-4'>
          <div className='flex align-items-center justify-content-flex-end input-label'>Gender:</div>

          <div className='flex align-items-center gap-8'>
            <div className='flex align-items-center'>
              <Checkbox
                title="gender-male"
                className='checkbox'
                inputProps={{ 'aria-label': 'primary checkbox' }}
                checked={patientGender === 'male'}
                onChange={handleChange.bind(this, 'patientGender', { target: { value: 'male' } })}
              />
              <label>Male</label>
            </div>
            <div className='flex align-items-center'>
              <Checkbox
                title="gender-female"
                className='checkbox'
                inputProps={{ 'aria-label': 'primary checkbox' }}
                checked={patientGender === 'female'}
                onChange={handleChange.bind(this, 'patientGender', { target: { value: 'female' } })}
              />
              <label>Female</label>
            </div>
          </div>
        </div>

        <Input name='patientHeight' label='Height:' className='flex' value={patientHeight} onChange={handleChange.bind(this, 'patientHeight')} />
        <label className='font-14 italic m-l-1 m-r-4'>(in.)</label>
        <Input name='patientWeight' label='Weight:' className='flex' value={patientWeight} onChange={handleChange.bind(this, 'patientWeight')} />
        <label className='font-14 italic m-l-1'>(lbs.)</label>
      </div>

      <div className='flex align-items-center'>
        <Input name='patientAddress' label='Patient Address:' className='flex flex-fill m-r-4' value={patientAddress} onChange={handleChange.bind(this, 'patientAddress')} />
        <Input name='patientState' label='State:' className='m-r-4' value={patientState} onChange={handleChange.bind(this, 'patientState')} />
        <Input name='patientZip' label='Zip:' value={patientZip} onChange={handleChange.bind(this, 'patientZip')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='patientCity' label='City:' className='flex flex-fill m-r-4' value={patientCity} onChange={handleChange.bind(this, 'patientCity')} />
        <Input name='patientEmail' label='Email:' className='min-width-260 m-r-4' value={patientEmail} onChange={handleChange.bind(this, 'patientEmail')} />
        <Input name='patientPhone' label='Phone:' value={patientPhone} onChange={handleChange.bind(this, 'patientPhone')} />
      </div>

      <div className='flex align-items-center d-none'>
        <Input name='patientInsuranceCompany' label='Insurance Company:' className='flex flex-fill m-r-4' value={patientInsuranceCompany} onChange={handleChange.bind(this, 'patientInsuranceCompany')} />
        <Input name='patientInsuranceNo' label='Insurance #:' value={patientInsuranceNo} onChange={handleChange.bind(this, 'patientInsuranceNo')} />
      </div>

      <div className='flex align-items-center d-none'>
        <Input name='patientDexcomId' label='Dexcom Patient # (for Dexcom use only):' className='flex flex-fill m-r-4' value={patientDexcomId} onChange={handleChange.bind(this, 'patientDexcomId')} />

        <div className='flex align-items-center gap-16'>
          <div className='flex align-items-center justify-content-flex-end input-label'>English is my primary language:</div>

          <div className='flex align-items-center gap-8'>
            <div className='flex align-items-center'>
              <Checkbox
                title="primary-language-english"
                className='checkbox'
                inputProps={{ 'aria-label': 'primary checkbox' }}
                checked={primaryLanguageEnglish === CheckBoxChecked.yes}
                onChange={handleChange.bind(this, 'primaryLanguageEnglish', { target: { value: CheckBoxChecked.yes } })}
              />
              <label>Yes</label>
            </div>
            <div className='flex align-items-center'>
              <Checkbox
                title="primary-language-is-no-english"
                className='checkbox'
                inputProps={{ 'aria-label': 'primary checkbox' }}
                checked={primaryLanguageEnglish === CheckBoxChecked.no}
                onChange={handleChange.bind(this, 'primaryLanguageEnglish', { target: { value: CheckBoxChecked.no } })}
              />
              <label>No</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
));

const PatientInformationV2 = memo(({
  value = {},
  onChange: handleChange = () => {},
}) => (
  <div className={`section patient-information m-t-8 m-b-5 p-4`}>
    <SectionLabel display='PATIENT INFORMATION' />

    <PatientNameAndDOB value={value} onChange={handleChange} />
  </div>
));

const ProviderInformation = memo(({
  value: {
    prescriberFullName,
    prescriberNpi,
    prescriberAddress,
    prescriberState,
    prescriberZip,
    prescriberCity,
    prescriberPhone,
    prescriberFax,
  },
  onChange: handleChange = () => {},
}) => (
  <div className={`section prescriber-information m-t-8 p-1`}>
    <SectionLabel display='PROVIDER INFORMATION' />

    <div className='flex flex-column gap-4'>
      <div className='flex align-items-center'>
        <Input name='prescriberFullName' label='Provider Name:' className='flex flex-fill m-r-4'  value={prescriberFullName} onChange={handleChange.bind(this, 'prescriberFullName')} />
        <Input name='prescriberNpi' label='NPI #:' value={prescriberNpi} onChange={handleChange.bind(this, 'prescriberNpi')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='prescriberAddress' label='Provider Address:' className='flex flex-fill m-r-4' value={prescriberAddress} />
        <Input name='prescriberState' label='State:' className='m-r-4' value={prescriberState} />
        <Input type='text' name='prescriberZip' label='Zip:' value={prescriberZip} />
      </div>

      <div className='flex align-items-center'>
        <Input name='prescriberCity' label='City:' className='flex flex-fill m-r-4' value={prescriberCity} />
        <Input name='prescriberPhone' label='Phone:' className='m-r-4' value={prescriberPhone} />
        <Input name='prescriberFax' label='Fax:' value={prescriberFax} />
      </div>
    </div>
  </div>
));

const ProviderInformationV2 = memo(({
  value: {
    hcpSignature = '',
  },
  onChange: handleChange = () => {},
}) => (
  <div className={`section patient-information m-t-8 m-b-5 p-4 ${pageElementSelector}`}>
    <SectionLabel display='PROVIDER INFORMATION' />

    <div className={`flex align-items-center`}>
      <Input name='hcpSignature' label="Provider's Full  Name:" className='flex flex-fill m-r-4' value={hcpSignature} onChange={handleChange.bind(this, 'hcpSignature')} />
    </div>
  </div>
));

const Diagnosis = memo(({
  value: {
    otherICD10Code,
  },
  onChange: handleChange = () => {},
}) => {
  return (
    <div className={`section diagnosis-information m-t-8`}>
      <SectionLabel display='DIAGNOSIS CODE' />

      <div className='flex flex-column gap-4'>
        <div className='flex flex-column'>
          <div className='flex flex-fill font-14 p-1 bg-light-primary'>DIAGNOSIS AND ICD-10 CODES</div>
        </div>

        <div className='flex flex-column flex-fill p-1'>
          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>
              <span className='bold m-r-1'>Examples of Diabetes Mellitus</span><span className='italic'>(not all inclusive)*:</span>
            </div>
            <div className='flex align-items-center justify-content-center align-center p-r-2 p-l-2 mn-col col-2'><span className='bold'>Underlying Conditions</span></div>
            <div className='flex align-items-center justify-content-center align-center p-r-2 p-l-2 mn-col col-3'><span className='bold'>T1</span></div>
            <div className='flex align-items-center justify-content-center align-center p-r-2 p-l-2 mn-col col-4'><span className='bold'>T2</span></div>
            <div className='flex align-items-center justify-content-center align-center p-r-2 p-l-2 mn-col col-5'><span className='bold'>Other Specified DM</span></div>
          </div>

          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>Hypoglycemia without coma</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-2 mn-col-border'>E08.649</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-3 mn-col-border'>E10.649</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-4 mn-col-border'>E11.649</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-5 mn-col-border'>E13.649</div>
          </div>

          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>Hyperglycemia</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-2 mn-col-border'>E08.65</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-3 mn-col-border'>E10.65</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-4 mn-col-border'>E11.65</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-5 mn-col-border'>E13.65</div>
          </div>

          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>Other specified complications</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-2 mn-col-border'>E08.69</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-3 mn-col-border'>E10.69</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-4 mn-col-border'>E11.69</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-5 mn-col-border'>E13.69</div>
          </div>

          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>Without complications</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-2 mn-col-border mn-col-border-bottom'>E08.9</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-3 mn-col-border mn-col-border-bottom'>E10.9</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-4 mn-col-border mn-col-border-bottom'></div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-5 mn-col-border mn-col-border-bottom'>E13.9</div>
          </div>

          <div className='flex align-items-center gap-4 m-t-1'>
            <div className='flex col-1 align-items-center gap-4'>
              <span className='input-label'>ICD-10 Code</span>
              <span className='italic font-14'>(fill in):</span>
              <span className='required font-24'>*</span>
            </div>
            <Input name='otherICD10Code' label='' className='flex flex-fill' value={otherICD10Code} onChange={handleChange.bind(this, 'otherICD10Code')} />
          </div>
        </div>
      </div>
    </div>
  );
});

const MedicalNecessity = memo(({
  value: {
    hbA1c,
    hhu,
    fingerSticks,
    injectPerDay,
    bgLevelLow,
    bgLevelHigh,
    ...value
  },
  onChange: handleChange = () => {},
}) => {
  return (
    <div className={`section diagnosis-information m-t-8 p-1`}>
      <SectionLabel display='STATEMENT OF MEDICAL NECESSITY' />

      <div className='flex flex-column gap-4'>
        <div className='flex align-items-center gap-16'>
          <div className='flex gap-16 min-width-320'>
            <div className='flex align-items-center input-label'>Currently on CGM:</div>

            <div className='flex align-items-center gap-8'>
              <div className='flex align-items-center'>
                <Checkbox
                  title="checkbox-icgm-yes"
                  className='checkbox'
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                  checked={value['checkbox-icgm'] === CheckBoxChecked.yes}
                  onChange={handleChange.bind(this, 'checkbox-icgm', { target: { value: CheckBoxChecked.yes } })}
                />
                <label>Yes</label>
              </div>
              <div className='flex align-items-center'>
                <Checkbox
                  title="checkbox-icgm-no"
                  className='checkbox'
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                  checked={value['checkbox-icgm'] === CheckBoxChecked.no}
                  onChange={handleChange.bind(this, 'checkbox-icgm', { target: { value: CheckBoxChecked.no } })}
                />
                <label>No</label>
              </div>
            </div>
          </div>

          <div className='flex flex-fill align-items-center'>
            <Input name='hbA1c' label='A1C:' className='flex' value={hbA1c} onChange={handleChange.bind(this, 'hbA1c')} />
          </div>
        </div>

        <div className='flex align-items-center gap-16'>
          <div className='flex gap-16 min-width-320'>
            <div className='flex align-items-center input-label'>On Insulin Pump:</div>

            <div className='flex gap-8'>
              <div className='flex align-items-center'>
                <Checkbox
                  title="checkbox-pump-yes"
                  className='checkbox'
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                  checked={value['checkbox-pump'] === CheckBoxChecked.yes}
                  onChange={handleChange.bind(this, 'checkbox-pump', { target: { value: CheckBoxChecked.yes } })}
                />
                <label>Yes</label>
              </div>
              <div className='flex align-items-center'>
                <Checkbox
                  title="checkbox-pump-no"
                  className='checkbox'
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                  checked={value['checkbox-pump'] === CheckBoxChecked.no}
                  onChange={handleChange.bind(this, 'checkbox-pump', { target: { value: CheckBoxChecked.no } })}
                />
                <label>No</label>
              </div>
            </div>
          </div>

          <div className='flex flex-fill align-items-center gap-16'>
            <div className='flex align-items-center'>
              <Input name='fingerSticks' label='#Fingersticks:' className='flex' inputClassName='width-70' value={fingerSticks} onChange={handleChange.bind(this, 'fingerSticks')} />
              <label className='font-14 italic m-l-1'>per day</label>
            </div>

            <div className='flex align-items-center'>
              <Input name='injectPerDay' label='#Injections:' className='flex' inputClassName='width-70' value={injectPerDay} onChange={handleChange.bind(this, 'injectPerDay')} />
              <label className='font-14 italic m-l-1'>per day</label>
            </div>
          </div>
        </div>

        <div className='flex align-items-center gap-16'>
          <div className='flex align-items-center min-width-320'>
            <Input name='hhu' label='Fasting Hyperglycemia:' className='flex' inputClassName='width-100' value={hhu} onChange={handleChange.bind(this, 'hhu')} />
            <label className='font-14 italic m-l-1'>mg/dL</label>
          </div>

          <div className='flex flex-fill flex align-items-center gap-16'>
            <div className='flex align-items-center gap-4'>
              <label className='input-label m-r-4'>Fluctuating of BG Level:</label>
              <label className=''>Low</label>
              <Input name='bgLevelLow' label='' className='flex' inputClassName='width-100' value={bgLevelLow} onChange={handleChange.bind(this, 'bgLevelLow')} />
              <label className='font-14 italic'>mg/dL</label>
            </div>

            <div className='flex align-items-center gap-4'>
              <label className=''>High</label>
              <Input name='bgLevelHigh' label='' className='flex' inputClassName='width-100' value={bgLevelHigh} onChange={handleChange.bind(this, 'bgLevelHigh')} />
              <label className='font-14 italic'>mg/dL</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const Considerations = memo(({
  value: {
    ...value
  },
  onChange: handleChange = () => {},
}) => {
  const ListItems = [
    {
      key: 'item-A',
      Template: () => (
        <div className='flex align-items-flex-start gap-8'>
          <Checkbox
            title="checkbox-poi"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={value['checkbox-poi']}
            onChange={(_, value) => { handleChange('checkbox-poi', { target: { value } }); }}
          />
          <div className='line-20'>A. Patient is on insulin therapy</div>
        </div>
      ),
      visible: true,
    },
    {
      key: 'item-B',
      Template: () => (
        <div className='flex align-items-flex-start gap-8'>
          <Checkbox
            title="checkbox-3-injections-pd"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={value['checkbox-3-injections-pd']}
            onChange={(_, value) => { handleChange('checkbox-3-injections-pd', { target: { value } }); }}
          />
          <div className='line-20'>B. Patient administers 3+ injections per day or on an insulin pump</div>
        </div>
      ),
      visible: true,
    },
    {
      key: 'item-C',
      Template: () => (
        <div className='flex align-items-flex-start gap-8'>
          <Checkbox
            title="checkbox-bg-4+"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={value['checkbox-bg-4+']}
            onChange={(_, value) => { handleChange('checkbox-bg-4+', { target: { value } }); }}
          />
          <div className='line-20'>C. Patient self checks BG 4+ times per day</div>
        </div>
      ),
      visible: true,
    },
    {
      key: 'item-D',
      Template: () => (
        <div className='flex align-items-flex-start gap-8'>
          <Checkbox
            title="checkbox-faitr-bool"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={value['checkbox-faitr-bool']}
            onChange={(_, value) => { handleChange('checkbox-faitr-bool', { target: { value } }); }}
          />
          <div className='line-20'>D. Patient's insulin treatment requires frequent adjustment by patient on the basis of BGM or CGM testing results.</div>
        </div>
      ),
      visible: true,
    },
    {
      key: 'item-E',
      Template: () => (
        <div className='flex align-items-flex-start gap-8'>
          <Checkbox
            title="checkbox-hhu-bool"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={value['checkbox-hhu-bool']}
            onChange={(_, value) => { handleChange('checkbox-hhu-bool', { target: { value } }); }}
          />
          <div className='line-20'>E. History of hypoglycemia unawareness, severe hypoglycemia resulting in third party intervention and/or hospitalization/ paramedical treatment.</div>
        </div>
      ),
      visible: true,
    },
    {
      key: 'item-F',
      Template: () => (
        <div className='flex align-items-flex-start gap-8'>
          <Checkbox
            title="checkbox-had-visit"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={value['checkbox-had-visit']}
            onChange={(_, value) => { handleChange('checkbox-had-visit', { target: { value } }); }}
          />
          <div className='line-20'>F. Within 6 months prior to ordering CGM, patient had in-person visit with treating practitioner to confirm that patient is diabetic and meets A-E above and to evaluate patient's diabetes control.</div>
        </div>
      ),
      visible: true,
    },
    {
      key: 'item-G',
      Template: () => (
        <div className='flex align-items-flex-start gap-8'>
          <Checkbox
            title="checkbox-motivated"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={value['checkbox-motivated']}
            onChange={(_, value) => { handleChange('checkbox-motivated', { target: { value } }); }}
          />
          <div className='line-20'>G. Patient is motivated and knowledgeable to use CGM, and adheres to a diabetes treatment plan.</div>
        </div>
      ),
      visible: true,
    },
  ];

  return (
    <div className={`section clinical-considerations m-t-8 p-1`}>
      <SectionLabel display='CLINICAL CONSIDERATIONS' />
  
      <ol className='flex flex-column gap-8 m-0 p-0' type='A'>
        {
          ListItems.map(({ Template, key }) => <Template key={key} />)
        }
      </ol>
    </div>
  );
});

const PrescriptionInformation = memo(({
  value: {
    lastVisitDate,
    orderDate,
    ...value
  },
  onChange: handleChange = () => {},
}) => {
  const GridRows = [
    {
      key: 'checkbox-g6-receiver',
      Template: () => (
        <Fragment>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary'>Dexcom G6 Receiver</div>
          <div className='flex align-items-center p-1 gap-8 border-left-15-primary border-top-15-primary'>
            <Checkbox
              title="checkbox-g6-receiver"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-g6-receiver'] === CheckBoxChecked.yes}
              onChange={(_, value) => { handleChange('checkbox-g6-receiver', { target: { value: CheckBoxValue.get(value) } }); }}
            />
            <div className='flex flex-column gap-8'>
              <div className='flex'>1 Receiver</div>
              <div className='flex font-12'>NDC: 08627-0091-11 A9278, K0554, E2103</div>
            </div>
          </div>
          <div className='flex align-items-center p-1 font-14 line-16 border-left-15-primary border-top-15-primary'>SIG: Use to monitor blood glucose. Displays glucose trends in 3 colors when glucose is high, low or within range.</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary bold'>1</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary border-right-15-primary bold'>0</div>
        </Fragment>
      ),
      visible: false,
    },
    {
      key: 'checkbox-g6-transmitter',
      Template: () => (
        <Fragment>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary'>Dexcom G6 Transmitter</div>
          <div className='flex align-items-center p-1 gap-8 border-left-15-primary border-top-15-primary'>
            <Checkbox
              title="checkbox-g6-transmitter"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-g6-transmitter'] === CheckBoxChecked.yes}
              onChange={(_, value) => { handleChange('checkbox-g6-transmitter', { target: { value: CheckBoxValue.get(value) } }); }}
            />
            <div className='flex flex-column gap-8'>
              <div className='flex'>1 Transmitter</div>
              <div className='flex font-12'>NDC: 08627-0016-01 A9277, K0553, A4239</div>
            </div>
          </div>
          <div className='flex align-items-center p-1 font-14 line-16 border-left-15-primary border-top-15-primary'>SIG: Change transmitter every 3 months. Fastened on top of the sensor and wirelessly sends data to the receiver or compatible smart device. For therapeutic CGM, include all necessary supplies and accessories.</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary bold'>1</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary border-right-15-primary bold'>4</div>
        </Fragment>
      ),
      visible: false,
    },
    {
      key: 'checkbox-g6-sensors',
      Template: () => (
        <Fragment>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary'>Dexcom G6 Sensors</div>
          <div className='flex align-items-center p-1 gap-8 border-left-15-primary border-top-15-primary'>
            <Checkbox
              title="checkbox-g6-sensors"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-g6-sensors'] === CheckBoxChecked.yes}
              onChange={(_, value) => { handleChange('checkbox-g6-sensors', { target: { value: CheckBoxValue.get(value) } }); }}
            />
            <div className='flex flex-column gap-8'>
              <div className='flex'>1 three-pack Sensors</div>
              <div className='flex font-12'>NDC: 08627-0053-03 A9276, K0553, A4239</div>
            </div>
          </div>
          <div className='flex align-items-center p-1 font-14 line-16 border-left-15-primary border-top-15-primary'>SIG: Change sensor every 10 days. Discreetly worn under clothing and measures glucose levels under skin. For therapeutic CGM, include all necessary supplies and accessories.</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary bold'>1</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary border-right-15-primary bold'>12</div>
        </Fragment>
      ),
      visible: false,
    },
    {
      key: 'checkbox-g7-receiver',
      Template: () => (
        <Fragment>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary'>Dexcom G7 Receiver</div>
          <div className='flex align-items-center p-1 gap-8 border-left-15-primary border-top-15-primary'>
            <Checkbox
              title="checkbox-g7-receiver"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-g7-receiver'] === CheckBoxChecked.yes}
              onChange={(_, value) => { handleChange('checkbox-g7-receiver', { target: { value: CheckBoxValue.get(value) } }); }}
            />
            <div className='flex flex-column gap-8'>
              <div className='flex'>1 Receiver</div>
              <div className='flex font-12'>NDC: 08627-0078-01 A9278, K0554, E2103</div>
            </div>
          </div>
          <div className='flex align-items-center p-1 font-14 line-16 border-left-15-primary border-top-15-primary'>SIG: Use to monitor blood glucose. Displays glucose trends in 3 colors when glucose is high, low or within range.</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary bold'>1</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary border-right-15-primary bold'>0</div>
        </Fragment>
      ),
      visible: true,
    },
    {
      key: 'checkbox-g7-sensors',
      Template: () => (
        <Fragment>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary border-bottom-15-primary'>Dexcom G7 Sensors</div>
          <div className='flex align-items-center p-1 gap-8 border-left-15-primary border-top-15-primary border-bottom-15-primary'>
            <Checkbox
              title="checkbox-g7-sensors"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-g7-sensors'] === CheckBoxChecked.yes}
              onChange={(_, value) => { handleChange('checkbox-g7-sensors', { target: { value: CheckBoxValue.get(value) } }); }}
            />
            <div className='flex flex-column gap-8'>
              <div className='flex'>1 one-pack 10-day sensor with Integrated Transmitter</div>
              <div className='flex font-12'>NDC: 08627-0077-01 A9276, A9277, K0553, A4239</div>
            </div>
          </div>
          <div className='flex align-items-center p-1 font-14 line-16 border-left-15-primary border-top-15-primary border-bottom-15-primary'>SIG: Change sensor with integrated transmitter wearable every 10 days. Quantity 1 = 10-day supply; Quantity 3 = 30-day supply; Quantity 9 = 90 day.</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary border-bottom-15-primary bold'>3</div>
          <div className='flex align-items-center p-1 border-left-15-primary border-top-15-primary border-right-15-primary border-bottom-15-primary bold'>12</div>
        </Fragment>
      ),
      visible: true,
    },
  ];

  return (
    <div className={`section clinical-considerations m-t-8 p-1`}>
      <SectionLabel display='Prescription Information' />
  
      <div className='flex flex-column gap-4'>
        <div className='grid auto-rows-50 col-5'>
          {
            GridRows.filter(({ visible }) => visible).map(({ Template, key }) => <Template key={key} />)
          }
        </div>
    
        <div className='flex align-items-center gap-16'>
          <div className='flex'>
            <DateWrapper name='lastVisitDate' label='Last office visit:' value={lastVisitDate} onChange={handleChange.bind(this, 'lastVisitDate')} />
          </div>
          <div className='flex'>
            <DateWrapper name='orderDate' label='Order date:' value={orderDate} onChange={handleChange.bind(this, 'orderDate')} />
          </div>
        </div>
      </div>
    </div>
  );
});

const ProviderSignature = memo(({
  value: {
    hcpSignature,
    signatureDate,
    brandMedicallyNecessary,
  },
  onChange: handleChange = () => {}
}) => (
  <div className={`section provider-signature m-t-8 p-1`}>
    <SectionLabel display='PROVIDER SIGNATURE' />

    <div className='flex flex-column gap-4'>
      <div className='flex align-items-center justify-content-center font-16 bold'>
        PLEASE COMPLETE THE PRESCRIPTION INFORMATION ACCORDING TO STATE RULES & REGULATIONS
      </div>

      <div className='flex align-items-center justify-content-center font-12 line-14 align-center'>
        The document serves as prescription and statement of medical necessity for the above referenced patient for a Dexcom Continuous Glucose Monitoring System (New/Replacement Sensors, Transmitters, and Receivers) and all associated diabetes supplies to be provided by Dexcom or authorized disignee.
      </div>

      <div className='flex align-items-center justify-content-center font-12 line-14 align-center'>
        I certify that I am the prescriber identified in the above section and the medical necessity information contained in this document is true, accurate/complete to the best of my knowledge.
      </div>

      <div className='flex align-items-flex-start'>
        <div className='flex align-items-flex-start flex-fill gap-16'>
          <div className='flex align-items-center min-height-30 min-width-180'>
            <span className='input-label'>Electronically approved by:</span>
          </div>

          <div className='flex flex-column flex-fill gap-4'>
            <SignatureWrapper
              name='hcpSignature'
              label=''
              value={hcpSignature}
              inputClassName='flex flex-fill'
              onChange={handleChange.bind(this, 'hcpSignature')}
            />
            <p className='flex font-10 line-14 m-0'>Substitution Permitted</p>
            <p className='flex font-10 line-14 m-0 d-none'>
              In Order for a brand name product to be dispensed, the prescriber must hand write "Brand Necessary" or "Brand Medically Necessary" in the space below.
            </p>
          </div>
        </div>

        <div className='flex flex-column align-items-flex-end min-width-320'>
          <DateWrapper
            name='signatureDate'
            label='Date:'
            type='datetime-local'
            disabled
            value={signatureDate}
            max={signatureDate}
          />
        </div>
      </div>

      <div className='flex align-items-center d-none'>
        <Input name='brandMedicallyNecessary' label='Brand Medically Necessary:' className='flex-fill' value={brandMedicallyNecessary} onChange={handleChange.bind(this, 'brandMedicallyNecessary')} />
      </div>
    </div>
  </div>
));

export const QuestionAnswers = memo(({
  value = {},
  qAndAs = [],
  className = '',
  heading: Heading = Fragment,
  topContent: TopContent = Fragment,
  footer: Footer = Fragment,
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => {
  return qAndAs.map(({ ProductId: pId, ProductTitle: title, QuestionAnswers: qAndAs = [] }) => (
    <div className={`page border-primary flex flex-column gap-20 max-width-1024 ${pageWithMaxPossibleWidthSelector} ${className} d-none`}>
      <div className='content flex flex-column flex-fill'>
        <TopContent value={value} onChange={handleChange} />

        <div key={pId} className='section provider-signature m-t-8 p-4'>
          <SectionLabel display={`MEDICAL QUESTIONNAIRE ANSWERED BY THE PATIENT FOR PRODUCT - ${title} - (${pId})`} />

          <div className='flex flex-column gap-16'>
            {
              qAndAs.map(({ QuestionId: qId, QuestionText: question, AnswerText: answer }, index = 0, questions = []) => (
                <div key={`${pId}-${qId}`} className={`flex flex-column gap-4 mn-col justify-content-center ${pageElementSelector}`}>
                  <span className='flex align-items-center bold line-20 font-16 font-roboto'>
                    Q. {question}
                  </span>
                  <span className='flex align-items-center line-20 font-16 font-roboto'>
                    Ans. {answer}
                  </span>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      <Footer onSubmit={handleSubmit} />
    </div>
  ));
});

const SectionA = memo(({
  value = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div className={`page border-primary ${pageSelector} ${className}`}>
    <Heading value={value} onChange={handleChange} />

    <div className={`content flex flex-column gap-4 ${pageElementSelector}`}>
      <ScriptInfo />
      <PatientInformation value={value} onChange={handleChange} />
      <ProviderInformation value={value} onChange={handleChange} />
      <Diagnosis value={value} onChange={handleChange} />
      <MedicalNecessity value={value} onChange={handleChange} />
      <Considerations value={value} onChange={handleChange} />
      <PrescriptionInformation value={value} onChange={handleChange} />
      <ProviderSignature value={value} onChange={handleChange} />
    </div>

    <Footer onSubmit={handleSubmit} />
  </div>
));

const SectionB = memo(({
  value = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  className = '',
  heading: Heading = Fragment,
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => {
  const { qAndAs } = useMemo(() => {
    try {
      const productIdsForPDFInSettings = productInfo.StoreFrontData.StorefrontApplicationSettings.get(PID_ProvidersAndCreatePDF);
      const productIds = productIdsForPDFInSettings.value.split(',').map((pId) => Number(pId));
      const allQAndAs = consultInfo.ProductConsultDetails;
      const qAndAs = allQAndAs.filter(({ ProductId }) => productIds.includes(ProductId));
      return { productIds, qAndAs };
    } catch(e) {
      return { productIds: [], qAndAs: [] };
    }
  }, [productInfo, consultInfo]);

  return (
    <QuestionAnswers
      value={value}
      qAndAs={qAndAs}
      productInfo={productInfo}
      patientInfo={patientInfo}
      consultInfo={consultInfo}
      className={className}
      heading={Heading}
      topContent={PatientInformationV2}
      footer={Footer}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
});

const SectionC = memo(({
  value = {},
  notes = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div className={`page border-primary flex flex-column max-width-1024 ${pageWithMaxPossibleWidthSelector} ${className} d-none`}>
    <div className='content flex flex-column flex-fill m-b-5'>
      <PatientInformationV2 value={value} onChange={handleChange} />

      <ProviderInformationV2 value={value} onChange={handleChange} />

      <SOAPNotes notes={notes} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
));

export const CMNTemplateForDexcom = ({
  id = '',
  value = {},
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  reference,
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div ref={reference} id={id} className={CMNDexcomTemplateIdentifier}>
    <SectionA
      value={value}
      className={className}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />

    <SectionC
      value={value}
      notes={notes}
      className={className}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />

    <SectionB
      value={value}
      productInfo={productInfo}
      patientInfo={patientInfo}
      consultInfo={consultInfo}
      className={className}
      heading={Heading}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  </div>
);

export const TemplateDownloadManager = ({
  id = '',
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  formField = { ...DEFAULT_FORMFIELD },

  getCmnPdfData = async () => {},
  setFormField = () => {}
}) => {
  const reference = useRef();

  const currentTime = useTime({ interval: 1 });

  const value = useMemo(() => ({
    ...formField,
    signatureDate: MaxDateAndTimeFn(),
  }), [formField, currentTime]);

  /**
   * @description create canvas of the template & print in a pdf file
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    preparePDF(
      CMNDexcomTemplateIdentifier,
      [pageSelector, pageWithMaxPossibleWidthSelector],
      Letter,
      pageElementSelector,
      ImageQuality[100],
      generatePDF
    ).then(() => {
      // code snippet if required
    });
  };

  const handleChange = (name, event) => {
    setFormField({ ...formField, [name]: event.target.value });
  };

  const loadData = async () => {
    const response  = await getCmnPdfData({
      OrderId: productInfo.OrderId,
      PaitentId: patientInfo.UserId,
      template: Dexcom,
    });

    const pdfData = (() => {
      try {
        return response.data.Data || DEFAULT_PDF_INFO();
        // return CMN_PDF_DATA;
      } catch(e) {
        return DEFAULT_PDF_INFO();
      }
    })();

    let nextFormField = {
      ...DEFAULT_FORMFIELD,
    };

    try {
      nextFormField = {
        ...nextFormField,
  
        patientDob: pdfData?.patientInfo?.dob ? moment(pdfData.patientInfo.dob).format(DateFormat) : '',
        patientFullName: `${pdfData?.patientInfo?.lastName}, ${pdfData?.patientInfo?.firstName}`,
        patientGender: typeof(patientInfo.Gender) === 'string' ? patientInfo.Gender.toLowerCase() : '',
        patientHeight: patientInfo.Height,
        patientWeight: patientInfo.Weight,
        patientAddress: pdfData?.patientInfo?.address,
        patientState: pdfData?.patientInfo?.state,
        patientZip: pdfData?.patientInfo?.zip,
        patientCity: pdfData?.patientInfo?.city,
        patientPhone: pdfData?.patientInfo?.phone,
        patientEmail: pdfData?.patientInfo?.email,
  
        prescriberPreName: pdfData?.prescriberInfo?.preName,
        prescriberFullName: `${pdfData?.prescriberInfo?.lastName}, ${pdfData?.prescriberInfo?.firstName}`,
        prescriberNpi: pdfData?.prescriberInfo?.npiNo,
  
        lastVisitDate: MaxDate,
        orderDate: MaxDate,
        // orderDate: productInfo.OrderDate ? moment(productInfo.OrderDate).format(DateFormat) : '',
  
        hcpSignature: `${pdfData?.provider?.signature?.prifix} ${pdfData?.provider?.signature?.firstName} ${pdfData?.provider?.signature?.lastNmae}`,
        signatureDate: MaxDateAndTimeFn(),

        fingerSticks: '',
        injectPerDay: '',
        hhu: 'Unknown',
      };
    } catch (e) {

    }

    // eslint-disable-next-line no-unused-vars
    const questionAnswersMap = new Map([[0, []]]);
    const productInfoWithQuestionAnswers = consultInfo.ProductConsultDetails.find(({ ProductId }) => ProductId === productInfo.pdfInfo.productId);
    if (productInfoWithQuestionAnswers && productInfoWithQuestionAnswers.QuestionAnswers) {
      for (let i = 0; i < productInfoWithQuestionAnswers.QuestionAnswers.length; i++) {
        const qAndA = productInfoWithQuestionAnswers.QuestionAnswers[i];
        let qAndAs = [qAndA];
        if (questionAnswersMap.has(qAndA.QuestionId)) {
          const _qAndAs = questionAnswersMap.get(qAndA.QuestionId);
          qAndAs = [..._qAndAs, qAndA];
        }
        questionAnswersMap.set(qAndA.QuestionId, qAndAs);
      }
    }

    // eslint-disable-next-line no-unused-vars
    const config = (() => {
      try {
        return new Map(productInfo.StoreFrontData.StorefrontApplicationSettings) || new Map([['', {}]]);
      } catch(e) {
        return new Map([['', {}]]);
      }
    })();

    console.log('@logger - initializing the pdf template', {
      config,
      pdfData,
      pdfInfo: productInfo.pdfInfo,
      questionAnswers: questionAnswersMap,
      patientInfo: patientInfo?.Gender,
      formField: nextFormField,
      consultInfo,
    });

    /**
     * Possible App settings value - 117446
     */
    if (config.has(QID_CGM)) {
      const checkboxIcgmIds = config.get(QID_CGM).value.split(',');
      for (let i = 0; i < checkboxIcgmIds.length; i++) {
        const qId = Number(checkboxIcgmIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          nextFormField['checkbox-icgm'] = CheckBoxChecked.no;

          const regex = /(using a cgm)/gmi;
          if (regex.test(qAndA.AnswerText)) {
            nextFormField['checkbox-icgm'] = CheckBoxChecked.yes;
            break;
          }
        }

        if (nextFormField['checkbox-icgm'] === CheckBoxChecked.yes) {
          break;
        }
      }
    }
    /**
     * Possible App settings value - 117452
     */
    if (config.has(QID_HbA1CInput)) {
      const inputHbA1cIds = config.get(QID_HbA1CInput).value.split(',');
      for (let i = 0; i < inputHbA1cIds.length; i++) {
        const qId = Number(inputHbA1cIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromHbA1CInput(qAndAs[j]);
          nextFormField.hbA1c = hasAnswer ? answer : 'Unknown';

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField.hbA1c !== 'Unknown') {
          break;
        }
      }
    }

    /**
     * Possible App settings value - 117443
     */
    if (config.has(QID_PumpCheckbox)) {
      const checkboxIds = config.get(QID_PumpCheckbox).value.split(',');
      for (let i = 0; i < checkboxIds.length; i++) {
        const qId = Number(checkboxIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          nextFormField['checkbox-pump'] = CheckBoxChecked.no;

          const regex = /(insulin using a pump)/gmi;
          if (regex.test(qAndA.AnswerText)) {
            nextFormField['checkbox-pump'] = CheckBoxChecked.yes;
            break;
          }
        }

        if (nextFormField['checkbox-pump'] === CheckBoxChecked.yes) {
          break;
        }
      }
    }

    if (config.has(QID_Fingersticks)) {
      const inputIds = config.get(QID_Fingersticks).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromFingerstricksInput(qAndAs[j]);
          nextFormField.fingerSticks = answer;

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField.fingerSticks !== '') {
          break;
        }
      }
    }

    if (config.has(QID_InjectInhaleInputs)) {
      const inputIds = config.get(QID_InjectInhaleInputs).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromInjectionsInput(qAndAs[j]);
          nextFormField.injectPerDay = answer;

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField.injectPerDay !== '') {
          break;
        }
      }
    }

    if (config.has(QID_FastingHyperglycemiaInputs)) {
      const inputIds = config.get(QID_FastingHyperglycemiaInputs).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromFastingHyperglycemiaInput(qAndAs[j]);
          nextFormField.hhu = hasAnswer ? answer : 'Unknown';

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField.hhu !== 'Unknown') {
          break;
        }
      }
    }

    if (config.has(QID_BGLevelHighInputs)) {
      const inputIds = config.get(QID_BGLevelHighInputs).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromFastingHyperglycemiaInput(qAndAs[j]);
          nextFormField.bgLevelHigh = hasAnswer ? answer : 'Unknown';

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField.bgLevelHigh !== 'Unknown') {
          break;
        }
      }
    }

    if (config.has(QID_BGLevelLowInputs)) {
      const inputIds = config.get(QID_BGLevelLowInputs).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromFastingHyperglycemiaInput(qAndAs[j]);
          nextFormField.bgLevelLow = hasAnswer ? answer : 'Unknown';

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField.bgLevelLow !== 'Unknown') {
          break;
        }
      }
    }

    if (config.has(QID_PatientOnInsulinCheckbox)) {
      const inputIds = config.get(QID_PatientOnInsulinCheckbox).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromPatientOnInsulinCheckbox(qAndAs[j]);
          nextFormField['checkbox-poi'] = answer;

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField['checkbox-poi']) {
          break;
        }
      }
    }

    if (config.has(QID_3InjectionsPDCheckbox)) {
      const inputIds = config.get(QID_3InjectionsPDCheckbox).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFrom3InjectionsPDCheckbox(qAndAs[j]);
          nextFormField['checkbox-3-injections-pd'] = answer;

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField['checkbox-3-injections-pd']) {
          break;
        }
      }
    }

    if (config.has(QID_PatientSelfChecksBGCheckbox)) {
      const inputIds = config.get(QID_PatientSelfChecksBGCheckbox).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromPatientSelfChecksBGCheckbox(qAndAs[j]);
          nextFormField['checkbox-bg-4+'] = answer;

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField['checkbox-bg-4+']) {
          break;
        }
      }
    }

    if (config.has(QID_FrequentAdjustmentsInTreatmentCheckbox)) {
      const inputIds = config.get(QID_FrequentAdjustmentsInTreatmentCheckbox).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromFAITRCheckbox(qAndAs[j]);
          nextFormField['checkbox-faitr-bool'] = answer;

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField['checkbox-faitr-bool']) {
          break;
        }
      }
    }

    if (config.has(QID_HypoglycemiaUnawarenessSymptomsCheckbox)) {
      const inputIds = config.get(QID_HypoglycemiaUnawarenessSymptomsCheckbox).value.split(',');
      for (let i = 0; i < inputIds.length; i++) {
        const qId = Number(inputIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromHHUCheckbox(qAndAs[j]);
          nextFormField['checkbox-hhu-bool'] = answer;

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField['checkbox-hhu-bool']) {
          break;
        }
      }
    }
    
    setFormField(nextFormField);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className='flex flex-column flex-fill'>
      <CMNTemplateForDexcom
        id={id}
        value={value}
        notes={notes}
        productInfo={productInfo}
        patientInfo={patientInfo}
        consultInfo={consultInfo}
        reference={reference}
        onSubmit={handleSubmit}
        onChange={handleChange}
        className='m-b-4 p-1'
      />
    </div>
  );
};

export { CMNDexcomTemplateIdentifier };

export default memo(TemplateDownloadManager);
